import { FieldValues } from 'react-hook-form';
import isNaN from 'lodash/isNaN';
import {
  defineApi,
  isNotFoundError,
  useProjectSpecificApiClient,
} from '../apiForm/api';

export enum CheckoutDeviceType {
  SCO = 'sco',
  GATEKEEPER = 'gatekeeper',
  POS = 'pos',
  'SCO-NG' = 'sco-ng',
}

export interface AppearanceConfigCoordinates {
  shop?: string;
  device?: string;
}

export interface UpdateAppearanceConfig {
  coordinates?: AppearanceConfigCoordinates;
  data: FieldValues;
  etag: string;
}

const sanitizeAppearanceConfig = (data: FieldValues) => {
  const repairedObject = data;
  if (repairedObject.cartDeletionTimeoutSeconds) {
    repairedObject.cartDeletionTimeout =
      repairedObject.cartDeletionTimeoutSeconds
        ? `PT${repairedObject.cartDeletionTimeoutSeconds}S`
        : '';
  }
  delete repairedObject.cartDeletionTimeoutSeconds;
  Object.keys(repairedObject).forEach((key) => {
    if (repairedObject[key] === '' || isNaN(repairedObject[key]))
      delete repairedObject[key];
  });
  const cleanedData: FieldValues = {
    ...repairedObject,
  };
  return cleanedData;
};

const useApi = defineApi({
  getAvailableDeviceTypes: (client) =>
    client
      .get<CheckoutDeviceType[]>('/available-types')
      .then((resp) => resp.data),
  updateAvailableDeviceTypes: (client, data: string[]) =>
    client.put('/available-types', data),
  getUiSchema: async (client) =>
    client
      .get<any>('/config/ui-schema', { params: { platform: 'portal' } })
      .then((resp) => resp.data),
  getAppearanceConfig: async (
    client,
    coordinates: AppearanceConfigCoordinates | null,
  ) => {
    try {
      const {
        data,
        headers: { etag },
      } = await client.get('/config/content', { params: coordinates });
      const duration = (data?.cartDeletionTimeout as string)?.match(
        /PT([0-9]+)S/,
      )?.[1];
      if (duration) data.cartDeletionTimeoutSeconds = Number(duration || '0');
      return { data, etag: etag || '' };
    } catch (e) {
      if (e instanceof Error && isNotFoundError(e)) {
        return { data: {}, etag: '' };
      }
      throw e;
    }
  },
  updateAppearanceConfig: async (
    client,
    { data, coordinates, etag }: UpdateAppearanceConfig,
  ) =>
    client.put('/config/content', sanitizeAppearanceConfig(data), {
      headers: {
        'If-Match': etag,
      },
      params: coordinates,
    }),
});

export default function useCheckoutDeviceApi() {
  const client = useProjectSpecificApiClient({ basePath: '/checkout-devices' });
  return useApi(client);
}
